import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Edit, Save, X, Package, ShoppingBag, Users, DollarSign, Truck, Eye, EyeOff, Printer, Image, Plus, Trash2, Layers, Download, XCircle, BarChart3, Ticket, Mail, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { CollectionsManager } from '../components/admin/CollectionsManager';
import { AnalyticsPanel } from '../components/admin/AnalyticsPanel';
import { WebsiteAnalyticsPanel } from '../components/admin/WebsiteAnalyticsPanel';
import { NewProductDialog } from '../components/admin/NewProductDialog';
import { CouponsManager } from '../components/admin/CouponsManager';
import { EmailAlertsPanel } from '../components/admin/EmailAlertsPanel';
import { AIUploaderPanel } from '../components/admin/AIUploaderPanel';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, logout, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ price: '', originalPrice: '', stock: 100, name: '', category: '', description: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [imageUpload, setImageUpload] = useState({ productId: null, loading: false });
  const [newProductOpen, setNewProductOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/admin/login');
    }
  }, [isAdmin, loading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, activeTab]);

  const fetchData = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      if (activeTab === 'products' || activeTab === 'dashboard') {
        const [productsRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/dashboard-stats`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setProducts(productsRes.data);
        setStats(statsRes.data);
      }
      if (activeTab === 'orders' || activeTab === 'dashboard') {
        const ordersRes = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } });
        setOrders(ordersRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setEditForm({
      price: product.price,
      originalPrice: product.originalPrice,
      stock: typeof product.stock === 'number' ? product.stock : 100,
      name: product.name,
      category: product.category,
      description: product.description || '',
    });
  };

  const handleToggleVisibility = async (product) => {
    const newVisible = product.visible === false ? true : false;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/products/${product.id}`, { visible: newVisible }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(products.map(p => p.id === product.id ? { ...p, visible: newVisible } : p));
      toast.success(newVisible ? 'Product is now visible on the website' : 'Product is now hidden from the website');
    } catch (error) {
      toast.error('Failed to toggle visibility');
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Permanently delete "${product.name}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/products/${product.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(products.filter(p => p.id !== product.id));
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleProductCreated = (product) => {
    if (product) setProducts(prev => [...prev, product]);
  };

  const handleSave = async (productId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/products/${productId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProducts(products.map(p => 
        p.id === productId ? { ...p, ...editForm, discount: calculateDiscount(editForm.price, editForm.originalPrice) } : p
      ));
      
      setEditingId(null);
      toast.success('Product updated successfully!');
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const calculateDiscount = (price, originalPrice) => {
    const discount = Math.round(((originalPrice - price) / originalPrice) * 100);
    return `${discount}% OFF`;
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOrders(orders.map(o => o.order_id === orderId ? { ...o, status: newStatus } : o));
      toast.success('Order status updated!');
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const printHtmlInIframe = (html) => {
    // Use a hidden iframe instead of window.open — popup-blocker-free, works on mobile
    const existing = document.getElementById('sachdeva-print-frame');
    if (existing) existing.remove();
    const iframe = document.createElement('iframe');
    iframe.id = 'sachdeva-print-frame';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document || iframe.contentDocument;
    if (!doc) {
      toast.error('Unable to open print window');
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    // Wait for content (images) to load before printing
    const triggerPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        toast.error('Print failed');
      }
    };
    if (iframe.contentWindow && iframe.contentWindow.document.readyState === 'complete') {
      setTimeout(triggerPrint, 300);
    } else {
      iframe.onload = () => setTimeout(triggerPrint, 300);
    }
  };

  const handlePrintInvoice = async (orderId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      printHtmlInIframe(generateInvoiceHTML(response.data));
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  const handlePrintShippingLabel = async (orderId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/orders/${orderId}/shipping-label`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      printHtmlInIframe(generateShippingLabelHTML(response.data));
    } catch (error) {
      toast.error('Failed to generate shipping label');
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm(`Cancel order ${orderId}? If it was paid and inventory was deducted, stock will be restored automatically. This action cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/orders/${orderId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(orders.map(o => o.order_id === orderId ? { ...o, status: 'cancelled' } : o));
      toast.success(`Order ${orderId} cancelled. Inventory restored if applicable.`);
      // Refresh dashboard stats (pending count etc.)
      try {
        const statsRes = await axios.get(`${API}/admin/dashboard-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(statsRes.data);
      } catch {}
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  const handleExportOrders = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/orders/export?period=all`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const now = new Date().toISOString().slice(0, 10);
      a.download = `sachdeva_sons_orders_${now}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Orders exported as CSV');
    } catch (error) {
      toast.error('Failed to export orders');
    }
  };

  const handleImageUpload = async (productId, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setImageUpload({ productId, loading: true });
    const token = localStorage.getItem('adminToken');

    try {
      let latestImages = null;
      for (const file of files) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const response = await axios.post(
          `${API}/admin/products/${productId}/upload-image`,
          { image: base64 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        latestImages = response.data.images;
      }

      if (latestImages) {
        setProducts(products.map(p =>
          p.id === productId ? { ...p, images: latestImages, image: latestImages[0] || p.image } : p
        ));
      }

      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded successfully!`);
    } catch (error) {
      toast.error('Failed to upload image(s)');
    } finally {
      setImageUpload({ productId: null, loading: false });
      event.target.value = '';
    }
  };

  const handleDeleteImage = async (productId, imageIndex) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(
        `${API}/admin/products/${productId}/images/${imageIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(products.map(p =>
        p.id === productId ? { ...p, images: response.data.images, image: response.data.images[0] || p.image } : p
      ));
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <p className="text-[#8B7D6B] font-montserrat">Loading...</p>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#FAF9F7] py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-seasons text-[#2C2C2C] mb-2">Admin Dashboard</h1>
            <p className="text-[#8B7D6B] font-montserrat">Manage Products & Orders - Sachdeva Sons</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-[#C4B5A0] text-[#C4B5A0] hover:bg-[#C4B5A0] hover:text-white">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Card className="bg-white border-none shadow-lg p-4">
              <div className="flex items-center gap-3">
                <div className="bg-[#F5F3F0] p-2 rounded-full">
                  <Package className="h-5 w-5 text-[#C4B5A0]" />
                </div>
                <div>
                  <p className="text-xs text-[#8B7D6B] font-montserrat">Products</p>
                  <p className="text-xl font-seasons text-[#2C2C2C]">{stats.total_products}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white border-none shadow-lg p-4">
              <div className="flex items-center gap-3">
                <div className="bg-[#F5F3F0] p-2 rounded-full">
                  <ShoppingBag className="h-5 w-5 text-[#C4B5A0]" />
                </div>
                <div>
                  <p className="text-xs text-[#8B7D6B] font-montserrat">Total Orders</p>
                  <p className="text-xl font-seasons text-[#2C2C2C]">{stats.total_orders}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white border-none shadow-lg p-4">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-50 p-2 rounded-full">
                  <Truck className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-[#8B7D6B] font-montserrat">Pending</p>
                  <p className="text-xl font-seasons text-[#2C2C2C]">{stats.pending_orders}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white border-none shadow-lg p-4">
              <div className="flex items-center gap-3">
                <div className="bg-[#F5F3F0] p-2 rounded-full">
                  <Users className="h-5 w-5 text-[#C4B5A0]" />
                </div>
                <div>
                  <p className="text-xs text-[#8B7D6B] font-montserrat">Customers</p>
                  <p className="text-xl font-seasons text-[#2C2C2C]">{stats.total_customers}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white border-none shadow-lg p-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-50 p-2 rounded-full">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#8B7D6B] font-montserrat">Revenue (Paid)</p>
                  <p className="text-xl font-seasons text-[#2C2C2C]">₹{(stats.total_revenue || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white border-none shadow-lg p-4">
              <div className="flex items-center gap-3">
                <div className="bg-[#F5F3F0] p-2 rounded-full">
                  <Package className="h-5 w-5 text-[#C4B5A0]" />
                </div>
                <div>
                  <p className="text-xs text-[#8B7D6B] font-montserrat">Units Sold</p>
                  <p className="text-xl font-seasons text-[#2C2C2C]" data-testid="stat-units-sold">{stats.units_sold || 0}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Analytics Panel */}
        <AnalyticsPanel />

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <Button
            onClick={() => setActiveTab('products')}
            variant={activeTab === 'products' ? 'default' : 'outline'}
            className={activeTab === 'products' ? 'bg-[#C4B5A0] text-white' : 'border-[#E8E5E0]'}
          >
            <Package className="h-4 w-4 mr-2" />
            Products
          </Button>
          <Button
            onClick={() => setActiveTab('orders')}
            variant={activeTab === 'orders' ? 'default' : 'outline'}
            className={activeTab === 'orders' ? 'bg-[#C4B5A0] text-white' : 'border-[#E8E5E0]'}
          >
            <ShoppingBag className="h-4 w-4 mr-2" />
            Orders
            {stats?.pending_orders > 0 && (
              <Badge className="ml-2 bg-yellow-500 text-white">{stats.pending_orders}</Badge>
            )}
          </Button>
          <Button
            onClick={() => setActiveTab('collections')}
            variant={activeTab === 'collections' ? 'default' : 'outline'}
            data-testid="tab-collections"
            className={activeTab === 'collections' ? 'bg-[#C4B5A0] text-white' : 'border-[#E8E5E0]'}
          >
            <Layers className="h-4 w-4 mr-2" />
            Collections
          </Button>
          <Button
            onClick={() => setActiveTab('website-analytics')}
            variant={activeTab === 'website-analytics' ? 'default' : 'outline'}
            data-testid="tab-website-analytics"
            className={activeTab === 'website-analytics' ? 'bg-[#C4B5A0] text-white' : 'border-[#E8E5E0]'}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Website Analytics
          </Button>
          <Button
            onClick={() => setActiveTab('coupons')}
            variant={activeTab === 'coupons' ? 'default' : 'outline'}
            data-testid="tab-coupons"
            className={activeTab === 'coupons' ? 'bg-[#C4B5A0] text-white' : 'border-[#E8E5E0]'}
          >
            <Ticket className="h-4 w-4 mr-2" />
            Coupons
          </Button>
          <Button
            onClick={() => setActiveTab('email-alerts')}
            variant={activeTab === 'email-alerts' ? 'default' : 'outline'}
            data-testid="tab-email-alerts"
            className={activeTab === 'email-alerts' ? 'bg-[#C4B5A0] text-white' : 'border-[#E8E5E0]'}
          >
            <Mail className="h-4 w-4 mr-2" />
            Email Alerts
          </Button>
          <Button
            onClick={() => setActiveTab('ai-uploader')}
            variant={activeTab === 'ai-uploader' ? 'default' : 'outline'}
            data-testid="tab-ai-uploader"
            className={activeTab === 'ai-uploader' ? 'bg-[#C4B5A0] text-white' : 'border-[#E8E5E0]'}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            AI Uploader
          </Button>
        </div>

        {/* Collections Tab */}
        {activeTab === 'collections' && <CollectionsManager />}

        {/* Website Analytics Tab */}
        {activeTab === 'website-analytics' && <WebsiteAnalyticsPanel />}

        {/* Coupons Tab */}
        {activeTab === 'coupons' && <CouponsManager />}

        {/* Email Alerts Tab */}
        {activeTab === 'email-alerts' && <EmailAlertsPanel />}

        {/* AI Uploader Tab */}
        {activeTab === 'ai-uploader' && <AIUploaderPanel />}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <Card className="bg-white border-none shadow-lg overflow-hidden">
            <div className="p-6 border-b border-[#F5F3F0] flex justify-between items-center flex-wrap gap-3">
              <h2 className="text-2xl font-seasons text-[#2C2C2C]">Products Inventory</h2>
              <Button
                onClick={() => setNewProductOpen(true)}
                data-testid="new-product-btn"
                className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white"
              >
                <Plus className="h-4 w-4 mr-2" /> New Product
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F5F3F0]">
                  <tr>
                    <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Product</th>
                    <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Category</th>
                    <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Price</th>
                    <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Original</th>
                    <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Stock</th>
                    <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Images</th>
                    <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Visible</th>
                    <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <React.Fragment key={product.id}>
                    <tr className="border-b border-[#F5F3F0] hover:bg-[#FAF9F7] transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {product.image ? (
                            <img src={product.image} alt={product.name} className="w-12 h-16 object-cover rounded" />
                          ) : (
                            <div className="w-12 h-16 rounded bg-[#F5F3F0] border border-dashed border-[#D4C4B0] flex items-center justify-center">
                              <Image className="h-4 w-4 text-[#C4B5A0]" />
                            </div>
                          )}
                          {editingId === product.id ? (
                            <Input
                              value={editForm.name}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              className="w-48"
                            />
                          ) : (
                            <span className="font-montserrat text-sm text-[#2C2C2C] max-w-xs">{product.name}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {editingId === product.id ? (
                          <Input
                            value={editForm.category}
                            onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                            className="w-32"
                          />
                        ) : (
                          <span className="text-sm font-montserrat text-[#8B7D6B]">{product.category}</span>
                        )}
                      </td>
                      <td className="p-4">
                        {editingId === product.id ? (
                          <Input
                            type="number"
                            value={editForm.price}
                            onChange={(e) => setEditForm({ ...editForm, price: parseInt(e.target.value) })}
                            className="w-24"
                          />
                        ) : (
                          <span className="font-montserrat text-[#2C2C2C]">₹{product.price}</span>
                        )}
                      </td>
                      <td className="p-4">
                        {editingId === product.id ? (
                          <Input
                            type="number"
                            value={editForm.originalPrice}
                            onChange={(e) => setEditForm({ ...editForm, originalPrice: parseInt(e.target.value) })}
                            className="w-24"
                          />
                        ) : (
                          <span className="font-montserrat text-[#8B7D6B]">₹{product.originalPrice}</span>
                        )}
                      </td>
                      <td className="p-4">
                        {editingId === product.id ? (
                          <Input
                            type="number"
                            value={editForm.stock}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              setEditForm({ ...editForm, stock: isNaN(v) ? 0 : v });
                            }}
                            className="w-24"
                          />
                        ) : (
                          <span className={`font-montserrat ${typeof product.stock === 'number' && product.stock <= 0 ? 'text-red-600 font-bold' : 'text-[#2C2C2C]'}`} data-testid={`product-stock-${product.id}`}>
                            {typeof product.stock === 'number' ? product.stock : 100}
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 flex-wrap" data-testid={`product-images-cell-${product.id}`}>
                          {(product.images && product.images.length > 0 ? product.images : [product.image]).map((img, idx) => (
                            <div key={idx} className="relative group">
                              <img src={img} alt={`${product.name} ${idx + 1}`} className="h-10 w-10 object-cover rounded border border-[#E8E2D5]" />
                              {product.images && product.images.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteImage(product.id, idx)}
                                  data-testid={`delete-image-${product.id}-${idx}`}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Delete image"
                                >
                                  <Trash2 className="h-2.5 w-2.5" />
                                </button>
                              )}
                            </div>
                          ))}
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={(e) => handleImageUpload(product.id, e)}
                              disabled={imageUpload.loading && imageUpload.productId === product.id}
                              data-testid={`upload-image-input-${product.id}`}
                            />
                            <Button size="sm" variant="ghost" className="text-[#C4B5A0]" asChild data-testid={`upload-image-btn-${product.id}`}>
                              <span>
                                {imageUpload.loading && imageUpload.productId === product.id ? (
                                  '...'
                                ) : (
                                  <Plus className="h-4 w-4" />
                                )}
                              </span>
                            </Button>
                          </label>
                        </div>
                      </td>
                      <td className="p-4">
                        <button
                          type="button"
                          onClick={() => handleToggleVisibility(product)}
                          title={product.visible === false ? 'Show on website' : 'Hide from website'}
                          data-testid={`visibility-toggle-${product.id}`}
                          className={`inline-flex items-center gap-1.5 text-xs font-montserrat px-2 py-1 rounded-full transition ${
                            product.visible === false
                              ? 'bg-red-50 text-red-700 hover:bg-red-100'
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}
                        >
                          {product.visible === false ? (
                            <><EyeOff className="h-3.5 w-3.5" /> Hidden</>
                          ) : (
                            <><Eye className="h-3.5 w-3.5" /> Visible</>
                          )}
                        </button>
                      </td>
                      <td className="p-4">
                        {editingId === product.id ? (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleSave(product.id)} className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white">
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="border-[#8B7D6B] text-[#8B7D6B]">
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(product)} className="text-[#C4B5A0] hover:text-[#B0A090]" data-testid={`edit-product-${product.id}`}>
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteProduct(product)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              title="Delete product"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {editingId === product.id && (
                      <tr className="bg-[#FAF9F7] border-b border-[#F5F3F0]">
                        <td colSpan={8} className="p-4">
                          <label className="block text-xs font-montserrat text-[#6B4E37] uppercase tracking-wider mb-1">Description</label>
                          <textarea
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            rows={2}
                            className="w-full bg-white border border-[#E8E2D5] rounded-md p-2 text-sm font-montserrat text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                            placeholder="Detailed product description shown on the product page..."
                            data-testid={`edit-description-${product.id}`}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <NewProductDialog
              open={newProductOpen}
              onClose={() => setNewProductOpen(false)}
              onCreated={handleProductCreated}
            />
          </Card>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <Card className="bg-white border-none shadow-lg overflow-hidden">
            <div className="p-6 border-b border-[#F5F3F0] flex justify-between items-center flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-seasons text-[#2C2C2C]">Order Management</h2>
                <p className="text-sm font-montserrat text-[#8B7D6B] mt-1">{orders.length} order{orders.length !== 1 ? 's' : ''} total</p>
              </div>
              <Button
                onClick={handleExportOrders}
                data-testid="export-orders-btn"
                className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white"
              >
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
            {orders.length === 0 ? (
              <div className="p-8 text-center">
                <ShoppingBag className="h-12 w-12 text-[#C4B5A0] mx-auto mb-4" />
                <p className="text-[#8B7D6B] font-montserrat">No orders yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F5F3F0]">
                    <tr>
                      <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Order ID</th>
                      <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Date</th>
                      <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Items</th>
                      <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Total</th>
                      <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Payment</th>
                      <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Status</th>
                      <th className="text-left p-4 font-montserrat text-sm text-[#6B4E37] uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.order_id} className="border-b border-[#F5F3F0] hover:bg-[#FAF9F7]">
                        <td className="p-4 font-montserrat text-sm">{order.order_id}</td>
                        <td className="p-4 font-montserrat text-sm text-[#8B7D6B]">
                          {new Date(order.created_at).toLocaleDateString('en-IN')}
                        </td>
                        <td className="p-4">
                          <div className="flex -space-x-2">
                            {order.items.slice(0, 3).map((item, idx) => (
                              <img key={idx} src={item.image} alt="" className="w-8 h-10 object-cover rounded border-2 border-white" />
                            ))}
                            {order.items.length > 3 && (
                              <div className="w-8 h-10 bg-[#F5F3F0] rounded border-2 border-white flex items-center justify-center text-xs">
                                +{order.items.length - 3}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4 font-seasons">₹{order.total_amount}</td>
                        <td className="p-4">
                          <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {order.payment_status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <select
                            value={order.status}
                            onChange={(e) => handleUpdateOrderStatus(order.order_id, e.target.value)}
                            className={`px-2 py-1 rounded text-xs font-montserrat ${getStatusColor(order.status)}`}
                          >
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={() => setSelectedOrder(order)} className="text-[#C4B5A0]" data-testid={`view-order-${order.order_id}`} title="View Order">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handlePrintInvoice(order.order_id)} className="text-[#C4B5A0]" data-testid={`print-invoice-${order.order_id}`} title="Print Invoice">
                              <Printer className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handlePrintShippingLabel(order.order_id)} className="text-[#C4B5A0]" data-testid={`print-shipping-${order.order_id}`} title="Print Shipping Label">
                              <Truck className="h-4 w-4" />
                            </Button>
                            {order.status !== 'cancelled' && order.status !== 'delivered' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCancelOrder(order.order_id)}
                                className="text-red-500 hover:text-red-700"
                                data-testid={`cancel-order-${order.order_id}`}
                                title="Cancel Order"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-seasons text-[#2C2C2C]">Order Details</h2>
                  <p className="text-[#8B7D6B] font-montserrat">{selectedOrder.order_id}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setSelectedOrder(null)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-montserrat text-sm text-[#6B4E37] mb-2">Shipping Address</h3>
                  <div className="bg-[#F5F3F0] p-4 rounded-lg">
                    <p className="font-montserrat">{selectedOrder.shipping_address.name}</p>
                    <p className="text-sm text-[#8B7D6B]">{selectedOrder.shipping_address.phone}</p>
                    <p className="text-sm text-[#8B7D6B]">
                      {selectedOrder.shipping_address.address_line1}, {selectedOrder.shipping_address.address_line2}
                    </p>
                    <p className="text-sm text-[#8B7D6B]">
                      {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} - {selectedOrder.shipping_address.pincode}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-montserrat text-sm text-[#6B4E37] mb-2">Items</h3>
                  <div className="space-y-3">
                    {selectedOrder.items.map((item, idx) => (
                      <div key={idx} className="flex gap-4 bg-[#F5F3F0] p-3 rounded-lg">
                        <img src={item.image} alt={item.name} className="w-16 h-20 object-cover rounded" />
                        <div className="flex-1">
                          <p className="font-montserrat text-sm">{item.name}</p>
                          <p className="text-xs text-[#8B7D6B]">Qty: {item.quantity}</p>
                          <p className="font-seasons">₹{item.price * item.quantity}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between font-seasons text-lg">
                    <span>Total</span>
                    <span>₹{selectedOrder.total_amount}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// Invoice HTML Generator
function generateInvoiceHTML(invoice) {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice - ${invoice.order_id}</title>
      <style>
        body { font-family: Georgia, serif; max-width: 800px; margin: 0 auto; padding: 20px; }
        .header { border-bottom: 2px solid #C4B5A0; padding-bottom: 20px; margin-bottom: 20px; }
        .header h1 { color: #2C2C2C; margin: 0; }
        .header p { color: #8B7D6B; margin: 5px 0; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
        .info-box { background: #F5F3F0; padding: 15px; border-radius: 8px; }
        .info-box h3 { color: #6B4E37; font-size: 12px; text-transform: uppercase; margin: 0 0 10px 0; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th { background: #F5F3F0; color: #6B4E37; text-transform: uppercase; font-size: 12px; padding: 10px; text-align: left; }
        td { padding: 10px; border-bottom: 1px solid #E8E5E0; }
        .total { font-size: 18px; text-align: right; margin-top: 20px; }
        .footer { margin-top: 40px; text-align: center; color: #8B7D6B; font-size: 12px; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${invoice.store.name}</h1>
        <p>${invoice.store.address}</p>
        <p>Phone: ${invoice.store.phone} | Email: ${invoice.store.email}</p>
      </div>

      <div class="info-grid">
        <div class="info-box">
          <h3>Invoice Details</h3>
          <p><strong>Order ID:</strong> ${invoice.order_id}</p>
          <p><strong>Date:</strong> ${new Date(invoice.date).toLocaleDateString('en-IN')}</p>
          <p><strong>Status:</strong> ${invoice.status}</p>
        </div>
        <div class="info-box">
          <h3>Ship To</h3>
          <p>${invoice.shipping_address.name}</p>
          <p>${invoice.shipping_address.address_line1}</p>
          <p>${invoice.shipping_address.city}, ${invoice.shipping_address.state} - ${invoice.shipping_address.pincode}</p>
          <p>Phone: ${invoice.shipping_address.phone}</p>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          ${invoice.items.map(item => `
            <tr>
              <td>${item.name}</td>
              <td>${item.quantity}</td>
              <td>₹${item.price}</td>
              <td>₹${item.price * item.quantity}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="total">
        <strong>Total Amount: ₹${invoice.total_amount}</strong>
      </div>

      <div class="footer">
        <p>Thank you for shopping with Sachdeva Sons!</p>
        <p>For queries, contact us at ${invoice.store.email}</p>
      </div>
    </body>
    </html>
  `;
}

// Shipping Label HTML Generator (4x6 inch thermal printer friendly)
function generateShippingLabelHTML(data) {
  const itemsSummary = data.items
    .map(i => `${i.name} × ${i.quantity}`)
    .join(' • ');
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Shipping Label - ${data.order_id}</title>
      <style>
        @page { size: 100mm 150mm; margin: 4mm; }
        body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 10px; color: #000; }
        .label { border: 2px solid #000; padding: 12px; }
        .row { display: flex; justify-content: space-between; border-bottom: 1px solid #000; padding: 6px 0; }
        .small { font-size: 11px; text-transform: uppercase; color: #555; letter-spacing: 1px; }
        .big { font-size: 18px; font-weight: bold; }
        .section { padding: 10px 0; border-bottom: 1px dashed #555; }
        .section:last-child { border-bottom: none; }
        .mono { font-family: monospace; font-size: 14px; }
        .cod { display: inline-block; padding: 4px 10px; background: #000; color: #fff; font-size: 12px; letter-spacing: 2px; }
        .prepaid { display: inline-block; padding: 4px 10px; background: #0a7a2b; color: #fff; font-size: 12px; letter-spacing: 2px; }
      </style>
    </head>
    <body>
      <div class="label">
        <div class="row">
          <span class="big">SACHDEVA SONS</span>
          <span>${data.cod ? '<span class="cod">COD</span>' : '<span class="prepaid">PREPAID</span>'}</span>
        </div>

        <div class="section">
          <div class="small">Ship To</div>
          <div class="big">${data.recipient.name || ''}</div>
          <div>${data.recipient.address_line1 || ''}</div>
          ${data.recipient.address_line2 ? `<div>${data.recipient.address_line2}</div>` : ''}
          <div>${data.recipient.city || ''}, ${data.recipient.state || ''} - ${data.recipient.pincode || ''}</div>
          <div>Phone: ${data.recipient.phone || ''}</div>
        </div>

        <div class="section">
          <div class="small">From</div>
          <div><strong>${data.sender.name}</strong></div>
          <div>${data.sender.address}</div>
          <div>Phone: ${data.sender.phone}</div>
        </div>

        <div class="section">
          <div class="row">
            <span class="small">Order ID</span>
            <span class="mono">${data.order_id}</span>
          </div>
          <div class="row">
            <span class="small">Total Items</span>
            <span class="mono">${data.total_items}</span>
          </div>
          <div class="row">
            <span class="small">Date</span>
            <span class="mono">${new Date(data.date).toLocaleDateString('en-IN')}</span>
          </div>
        </div>

        <div class="section">
          <div class="small">Items</div>
          <div style="font-size: 12px;">${itemsSummary}</div>
        </div>
      </div>
    </body>
    </html>
  `;
}

